// TODO: TS: check ts

import { fetchTenantConfig } from "features/white-labeling/api/fetchTenantConfig";
import { TenantConfigError } from "features/white-labeling/components/TenantConfigError";
import { setTenantConfig } from "features/white-labeling/lib/tenantConfig";
import React, { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

import { Loading } from "../components";
import { getActiveTheme } from "./themeSelection";
import { enakonTheme } from "./tokens/enakon/Theme/theme";
import { leaftechTheme } from "./tokens/leaftech/theme";
import { wagoTheme } from "./tokens/wago/theme";

export type Theme = any;

export const themes = {
  enakon: enakonTheme,
  leaftech: leaftechTheme,
  wago: wagoTheme,
};

export const defaultTheme = leaftechTheme;

type TenantBootstrapState = "error" | "loading" | "ready";

const ThemeContext = createContext<null | Theme>(null);

export const useTheme = (): Theme => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider after tenant config has loaded.");
  }
  return context;
};

const generateCSSVariables = (theme: any) => {
  const cssVars: Record<string, string> = {};
  cssVars["--font-primary"] = `"${theme.fonts.primary}"`;
  cssVars["--font-heading"] = `"${theme.fonts.heading}"`;

  return cssVars;
};

const applyCSSVariables = (cssVars: Record<string, string>) => {
  const root = document.documentElement;
  Object.entries(cssVars).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

const isValidTenantTheme = (themeName?: string) => Boolean(themeName && Object.keys(themes).includes(themeName));

export const ThemeProvider = ({ children }: { children?: ReactNode }) => {
  const [bootstrapState, setBootstrapState] = useState<TenantBootstrapState>("loading");

  useEffect(() => {
    let isMounted = true;

    fetchTenantConfig()
      .then((config) => {
        if (!isMounted) return;

        if (!isValidTenantTheme(config.theme)) {
          console.error("Tenant config returned an unsupported theme:", config.theme);
          setBootstrapState("error");
          return;
        }

        setTenantConfig(config);
        setBootstrapState("ready");
      })
      .catch((error) => {
        console.error("Failed to load tenant config.", error);
        if (!isMounted) return;
        setBootstrapState("error");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const theme: null | Theme = useMemo(() => (bootstrapState === "ready" ? getActiveTheme() : null), [bootstrapState]);

  const cssVars = useMemo(() => (theme ? generateCSSVariables(theme) : {}), [theme]);

  useEffect(() => {
    if (!theme) return;

    document.documentElement.setAttribute("data-theme", theme.name);
    applyCSSVariables(cssVars);

    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon && theme.favicon) {
      favicon.href = theme.favicon;
    }

    if (theme.logo?.alt) {
      document.title = `${theme.logo.alt} - User Panel`;
    }
  }, [cssVars, theme]);

  if (bootstrapState === "loading") return <Loading />;

  if (bootstrapState === "error" || !theme) return <TenantConfigError />;

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};
